<script setup>

import {computed, onMounted, reactive, watch} from "vue"

import CounterNumberValue from "@shared/components/atoms/CounterNumberValue.vue"
import AlertModal from "@shared/components/modals/AlertModal.vue"
import {useAccountStore} from "@shared/stores/AccountStore"
import {useGamePlayStore} from "@shared/stores/GamePlayStore"
import {useTournamentStore} from "@shared/stores/TournamentStore"
import useHelpers from "@shared/composables/useHelpers"

const accountStore = useAccountStore()
const gamePlayStore = useGamePlayStore()
const tournamentStore = useTournamentStore()
const {sanitizeHtml} = useHelpers()

const state = reactive({
    activeTab: 'DETAILS',
    isJoiningTournamentConfirmationVisible: false,
    refreshInterval: null
})

const tournament = computed(() => tournamentStore.tournament)

const stageDetails = computed(() => tournamentStore.getStageDetails(tournament.value))

const tournamentPlayers = computed(() => tournamentStore.getUniquePlayers(tournament.value))

const handleJoinTournamentClick = () => {
    if(tournament.value.buy_in) {
        state.isJoiningTournamentConfirmationVisible = true
    } else {
        handleJoinTournament()
    }
}

const handleJoinTournament = () => {
    state.activeTab = 'GAMES'
    state.isJoiningTournamentConfirmationVisible = false
    tournamentStore.fetchJoinTournament()
}

const formatTournamentDate = date => {
    const d = new Date(date)
    return `${d.getDate().toString().padStart(2, '0')} ${d.toLocaleString('default', {month: 'short'})} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
}

const formatPrize = prize => {
    let result = prize.position + ". "
    if (tournament.value.status == 'pending') {
        result += 'Pending'
    } else if (prize.userid) {
        result += prize.userid == accountStore.user.id ? `<span>${accountStore.user.name}</span>` : getUserPrize(prize)?.name
    }
    return result
}

const getUserPrize = prize => tournament
    .value
    .tournament_users
    .find(user => user.user_id === prize.user_id)?.user || false

const getTournamentPrizeValue = prize => `${(prize.percentage / 100) * tournament.value.actual_prize_pool}`

const getPlayerPrize = tournamentUser => parseFloat(
    tournament
    .value
    .prizes
    .find(prize => prize.user_id === tournamentUser.user_id) || 0.00
)

const getPlayerName = tournamentUser => {
    const currentUser = accountStore.user
    const name = tournamentUser.user_id == currentUser.id ? currentUser.name : tournamentUser.user.name
    let nameString = `${tournamentUser.position}. ${name}`

    if (tournament.value.status === 'completed') {
        const prize = getPlayerPrize(tournamentUser)
        if (prize) {
            const prizeValue = (prize.percentage / 100) * tournament.value.actual_prize_pool
            nameString += ` <span>(${prizeValue})</span>`
        }
    }

    return nameString
}

const handlePlayGame = gameId => {
    tournamentStore.setTournamentModalVisibility(false)
    gamePlayStore.playGame(gameId)
}

const handleCloseTournament = () => {
    stopTournamentRefresh()
    tournamentStore.setTournamentModalVisibility(false)
}

const stopTournamentRefresh = () => {
    if(state.refreshInterval) {
        clearInterval(state.refreshInterval)
    }
}

const startRefreshing = () => {
    stopTournamentRefresh()
    state.refreshInterval = setInterval(() => tournamentStore.refetchCurrentTournament(), 5000)
}

onMounted(() => {
    setTimeout(() => startRefreshing(), 5000)
})

watch(
    () => tournamentStore.isTournamentProgressWatching,
    (isTournamentProgressWatching) => {
        if (isTournamentProgressWatching) {
            stopTournamentRefresh()
        } else {
            startRefreshing()
        }
    }
)

</script>

<template>
    <div
        id="divAppTournamentDetails"
        v-if="tournament && tournamentStore.isTournamentModalVisible"
        :class="{
            'show': true,
            [state.activeTab.toLowerCase()]: true
        }"
    >
        <div class="divTournamentsOverlay"></div>
        <div class="divTournamentsPopup" style="width: unset; max-width: 400px;">
            <div id="divTournamentHeading">{{ tournament.name }}</div>
            <div
                id="btnCloseTournaments"
                @click="handleCloseTournament"
            />
            <div id="tbnTournamentsView" style="width: 380px;">
                <div class="divTabNavigator fill" style="display: flex; flex-direction: row; justify-content: center;">

                    <div
                        v-for="(tab, index) in ['DETAILS', 'GAMES', 'PAYOUT', 'PLAYERS']"
                        :class="{
                            'divButton': true,
                            'selected': state.activeTab === tab
                        }"
                        :key="`tournament-tab-${index}`"
                        :data-index="index"
                        @click="state.activeTab = tab"
                    >{{ tab }}
                    </div>
                </div>
            </div>
            <div class="divDetailsPanel">
                <div class="divTournamentDetailsLeft">
                    <div
                        id="divTournamentStage"
                        :style="{
                            border: `1px solid ${stageDetails.color}`,
                        }"
                    >
                        <div
                            id="divTournamentStageLabel"
                            :style="{
                                backgroundColor: stageDetails.color,
                            }"
                        >
                            {{ stageDetails.type }}
                        </div>
                        <div id="divTournamentStageDetail">
                            {{ stageDetails.status}}<br>
                            <span>{{ stageDetails.info }}</span>
                        </div>
                    </div>
                    <div class="divTournamentGuaranteed">
                        <div class="divTournamentGuaranteedLabel">
                            Guaranteed
                        </div>
                        <div id="divTournamentGuaranteedDetail">
                            <CounterNumberValue
                                :count="tournament.guaranteed"
                                prefix="R"
                            />
                        </div>
                    </div>
                </div>
                <div id="divTournamentDetails">
                    <div class="null"><span>Time:</span>
                        {{ formatTournamentDate(tournament.tournament_start) }}
                        -
                        {{ formatTournamentDate(tournament.tournament_end) }}
                    </div>
                    <div class="null"><span>Players:</span> {{ tournamentStore.getUniquePlayers(tournament).length }}</div>
                    <div class="null"><span>Games:</span> {{ tournament.games.length }}</div>
                    <div class="null">
                        <span>Re-buy:</span>
                        <template v-if="tournament.buy_in_duration">
                            Every {{ tournament.buy_in_duration }} mins
                        </template>
                        <template v-else>No</template>
                    </div>
                </div>

                <button
                    v-if="tournamentStore.isRegistrationOpen && !tournamentStore.isCurrentTournament"
                    id="btnBuyIn"
                    class="show"
                    @click="handleJoinTournamentClick"
                >
                    BUY IN FOR
                    <CounterNumberValue
                        v-if="tournament.buy_in"
                        :count="tournament.buy_in"
                        prefix="R"
                    />
                    <template v-else>FREE</template>
                </button>
                <button
                    v-else-if="tournamentStore.isTournamentRunning && tournamentStore.isCurrentTournament"
                    id="btnBuyIn"
                    class="show"
                    @click="() => state.activeTab = 'GAMES'"
                >
                    PLAY NOW
                </button>

                <div id="divTournamentDescription">
                    {{ tournament.description }}
                </div>
            </div>
            <div class="divGamesPanel">
                <div class="divTournamentColumnHeadings">
                    <div>Game</div>
                    <div></div>
                </div>
                <div id="divTournamentGames">

                    <div
                        v-for="game in tournament.games"
                        :key="`tournament-game-${game.id}`"
                        class="divTournamentGame"
                        @click="handlePlayGame(game.game_id)"
                    >
                        <div
                            class="divGameImage"
                            :style="{
                                backgroundImage: `url('${game.url_thumb}')`
                            }"
                        />
                        <div class="divGameName">{{ game.name }}</div>
                        <div class="divGameProvider">{{ game.product }}</div>
                    </div>
                </div>
            </div>
            <div class="divPayoutPanel">
                <div class="divTournamentColumnHeadings">
                    <div>Place</div>
                    <div>Prize</div>
                </div>
                <div id="divTournamentPrizes">
                    <div
                        v-for="prize in tournament.prizes"
                        :key="`tournament-prize-${prize.id}`"
                        class="divTournamentPrize"
                    >
                        <div class="divPrizePosition">
                            <div v-html="sanitizeHtml(formatPrize(prize))" />
                        </div>
                        <div class="divPrizePayout">
                            <CounterNumberValue :count="getTournamentPrizeValue(prize)" prefix="R" />
                            {{ parseFloat(prize.percentage) }}%
                        </div>
                    </div>
                </div>
            </div>
            <div class="divPlayersPanel">
                <div class="divTournamentColumnHeadings">
                    <div>Position</div>
                    <div>Points</div>
                </div>
                <div id="divTournamentPlayers">
                    <template
                        v-for="player in tournamentPlayers"
                        :key="`tournament-player-${player.id}`"
                    >
                        <div class="divTournamentPlayer" :class="{'userPlayer': player.user.id === accountStore.user.id}">
                            <div>{{ getPlayerName(player) }}</div>
                            <div>{{ (+player.points).toFixed(2) }}</div>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>

    <AlertModal
        v-if="state.isJoiningTournamentConfirmationVisible"
        cancel-text="No"
        confirm-text="Yes"
        @click:confirm="handleJoinTournament"
        @click:cancel="() => state.isJoiningTournamentConfirmationVisible = false"
    >
        Are you sure you would like to buy in to this tournament for
        <span class='spnGreen'>
            <CounterNumberValue
                :count="tournament.buy_in"
                :decimal-places=2
                prefix="R"
            />
        </span>?
    </AlertModal>
</template>

<style>
    #divAppTournamentDetails {
        z-index: 2 !important;
    }
</style>
